import axios from "axios";
import { reloadAfterCompanySwitch } from "hooks/companySwitchLocation";
import { useDebounce } from "hooks/useDebounce";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { useCallback, useEffect, useMemo, useState } from "react";
import { changeAdminCompany, getCompanyList } from "store/adminPanelSlice";
import type { CompanyLightInfo } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TPortalType } from "store/types";

export type SearchMode = "company" | "data";

export type TitleSearchResult = {
    id: number;
    name: string;
    ean13?: string | null;
    sku?: string | null;
    avatarUrl?: string | null;
};

export type BeneficiarySearchResult = {
    id: number;
    name: string | null;
    surname: string | null;
    pseudonym?: string | null;
    code?: string | null;
    avatarUrl?: string | null;
};

export type ArticleSearchResult = {
    id: number;
    name: string | null;
    titleId: number;
    titleName?: string | null;
    reference?: string | null;
    beneficiarySurname?: string | null;
    beneficiaryName?: string | null;
};

export type SearchResults = {
    titles: TitleSearchResult[];
    articles: ArticleSearchResult[];
    beneficiaries: BeneficiarySearchResult[];
} | null;

// Unified company type for command palette
export type CommandPaletteCompany = {
    id: number;
    name: string;
    avatarUrl: string | null;
    publisherType?: string | null;
};

type Params = {
    open: boolean;
    onClose: () => void;
    navigateTo?: (path: string, state?: any) => void;
    portalType?: TPortalType;
};

const PRESS_LIKE_PORTALS: TPortalType[] = ["press", "audiovisual"];

export function useCommandPaletteState({ open, onClose, navigateTo, portalType = "book" }: Params) {
    const isPressLike = PRESS_LIKE_PORTALS.includes(portalType);
    const dispatch = useAppDispatch();
    const { userData, companySettings } = useAppSelector((state) => state.settings);
    const adminCompaniesState = useAppSelector((state) => state.adminPanel.companies);
    const [query, setQuery] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState<SearchResults>(null);
    const [mode, setMode] = useState<SearchMode>("data");

    const debouncedQuery = useDebounce(query, 200);

    const userCompanies: CompanyLightInfo[] = useMemo(() => userData?.companies ?? [], [userData?.companies]);
    const hasMultipleCompanies = userCompanies.length > 1;
    const isCrealoAdmin = userData?.type === "admin" && (userData?.email ?? "").includes("@crealo.app");

    const adminCompanies = useMemo(() => adminCompaniesState.data ?? [], [adminCompaniesState.data]);
    const isLoadingCompanies = adminCompaniesState.isLoading;

    // Load all companies via Redux if user is Crealo admin
    useEffect(() => {
        if (isCrealoAdmin && open && mode === "company") {
            // Only fetch if we don't already have companies loaded
            if (!adminCompanies || adminCompanies.length === 0) {
                dispatch(getCompanyList());
            }
        }
    }, [isCrealoAdmin, open, mode, adminCompanies, dispatch]);

    // Use all companies from adminPanel if Crealo admin, otherwise use user companies
    const companies: CommandPaletteCompany[] = useMemo(() => {
        if (isCrealoAdmin && mode === "company" && adminCompanies.length > 0) {
            return adminCompanies.map((company) => ({
                id: company.id,
                name: company.name,
                avatarUrl: company.avatarUrl ?? null,
                publisherType: company.publisherType ?? null,
            }));
        }
        // Map user companies to unified format
        return userCompanies.map((company) => ({
            id: company.companyId,
            name: company.name,
            avatarUrl: company.avatarUrl ?? null,
            publisherType: companySettings?.publisherType ?? null,
        }));
    }, [isCrealoAdmin, mode, adminCompanies, userCompanies, companySettings]);

    const filteredCompanies = useMemo(() => {
        if (!query.trim()) return companies;
        const lower = query.toLowerCase();
        return companies.filter((company) => {
            const matchesName = company.name.toLowerCase().includes(lower);
            const matchesId = company.id.toString().includes(query.trim());
            return matchesName || matchesId;
        });
    }, [companies, query]);

    // Use infinite scroll for companies when in company mode and no search query
    const { paginatedData: paginatedCompanies, paginatedElRef: companyPaginatedElRef } = useInfiniteScroll(
        mode === "company" && !query.trim() ? filteredCompanies : null,
        open && mode === "company",
        20, // itemsPerPage
    );

    useEffect(() => {
        if (!open) {
            setQuery("");
            setSearchResults(null);
            setIsSearching(false);
            return;
        }
        // Always default to data mode on open
        setMode("data");
    }, [open]);

    // Note: We allow company mode for Crealo admins even with single company
    // because they can see all companies in the system

    useEffect(() => {
        if (mode !== "data" || !debouncedQuery.trim()) {
            setSearchResults(null);
            setIsSearching(false);
            return;
        }

        const controller = new AbortController();
        const fetchSearch = async () => {
            try {
                setIsSearching(true);
                const response = await axios.get(`${import.meta.env.REACT_APP_API_ENDPOINT}/commandpalette/search`, {
                    params: { q: debouncedQuery, limit: 3 },
                    signal: controller.signal,
                });
                setSearchResults(response.data.data);
            } catch (error: any) {
                if (error.name !== "CanceledError" && error.name !== "AbortError") {
                    console.error("Global search failed", error);
                }
            } finally {
                setIsSearching(false);
            }
        };

        fetchSearch();

        return () => {
            controller.abort();
        };
    }, [debouncedQuery, mode]);

    const navigateOrRedirect = (path: string, state?: any) => {
        if (navigateTo) {
            navigateTo(path, state);
        } else {
            // Fallback to full reload if router navigation is not provided
            window.location.href = path;
        }
    };

    const handleSelectCompany = async (companyId: number) => {
        if (companyId === companySettings?.id) {
            onClose();
            return;
        }

        // Same flow as AdminPanel CompaniesList: updates Cognito, logs access, links account as owner
        const result = await dispatch(changeAdminCompany({ companyId, reason: "" }));
        if (!changeAdminCompany.fulfilled.match(result)) {
            onClose();
            return;
        }

        localStorage.setItem("companyId", companyId.toString());
        sessionStorage.setItem("payment_page_data", "{}");
        onClose();
        reloadAfterCompanySwitch();
    };

    const handleOpenTitle = (id: number) => {
        navigateOrRedirect(`/titles/${id}`);
        onClose();
    };

    const handleOpenBeneficiary = (id: number) => {
        navigateOrRedirect(`/beneficiars/${id}`);
        onClose();
    };

    const handleCreateTitle = () => {
        navigateOrRedirect("/new-title");
        onClose();
    };

    const handleCreateBeneficiary = () => {
        navigateOrRedirect("/beneficiaries/new");
        onClose();
    };

    const handleCreatePublication = () => {
        navigateOrRedirect("/publications/new");
        onClose();
    };

    const handleCreateArticle = () => {
        navigateOrRedirect("/articles/new");
        onClose();
    };

    const handleOpenPublication = (id: number) => {
        navigateOrRedirect(`/publications/${id}`);
        onClose();
    };

    const handleOpenArticle = (titleId: number, articleId: number) => {
        navigateOrRedirect(`/publications/${titleId}`, { articleId });
        onClose();
    };

    const handleFilterTitleInManagement = (ean13?: string | null, name?: string | null) => {
        const queryValue = (ean13 || name || "").trim();
        if (!queryValue) return;
        // Store search query in localStorage for useAPIPagination to pick up
        localStorage.setItem("commandPaletteSearchQuery", queryValue);
        navigateOrRedirect("/management");
        onClose();
    };

    const handleFilterBeneficiaryInManagement = (surname?: string | null, name?: string | null) => {
        const fullName = [surname, name].filter(Boolean).join(" ").trim();
        if (!fullName) return;
        // Store search query in localStorage for useAPIPagination to pick up
        localStorage.setItem("commandPaletteSearchQuery", fullName);
        navigateOrRedirect("/management");
        onClose();
    };

    const handleFilterArticleInManagement = (name?: string | null) => {
        const queryValue = (name || "").trim();
        if (!queryValue) return;
        localStorage.setItem("commandPaletteSearchQuery", queryValue);
        navigateOrRedirect("/management");
        onClose();
    };

    const toggleMode = useCallback(() => {
        setMode((current) => (current === "data" ? "company" : "data"));
    }, []);

    // Use paginated companies when no search query, otherwise use filtered companies
    const companiesToDisplay = mode === "company" && !query.trim() ? paginatedCompanies : filteredCompanies;

    return {
        mode,
        setMode,
        query,
        setQuery,
        debouncedQuery,
        isSearching,
        searchResults,
        filteredCompanies: companiesToDisplay,
        totalCompaniesCount: companies.length,
        hasMultipleCompanies,
        isCrealoAdmin,
        companySettings,
        isLoadingCompanies,
        companyPaginatedElRef,
        handleSelectCompany,
        handleOpenTitle,
        handleOpenBeneficiary,
        handleCreateTitle,
        handleCreateBeneficiary,
        handleCreatePublication,
        handleCreateArticle,
        handleOpenPublication,
        handleOpenArticle,
        handleFilterTitleInManagement,
        handleFilterBeneficiaryInManagement,
        handleFilterArticleInManagement,
        toggleMode,
        isPressLike,
        portalType,
    };
}
